import {memo, useEffect, useRef} from 'react'
import {css} from '@emotion/react'
import {getScrollbarSize} from '@/script/scrollbar.mjs'
import {useTreeDocContext} from '@/components/TreeDoc/index.mjs'

const cssNodeText = css({
    boxSizing: 'content-box',
    position: 'relative',
    maxWidth: 300,
    lineHeight: 1.2,
    textDecorationThickness: '0.1em',
    whiteSpace: 'pre-wrap',

    '& :empty::before': {
        content: '"\u200d"',
    },

    '& :focus-visible': {
        outline: 'none',
    },
})

const InputText = ({isEditing, node, text, ...props}) => {
    const map = useTreeDocContext()
    const refEl = useRef()
    const refLastText = useRef()

    useEffect(
        () => {
            if (! isEditing) {
                return
            }

            // 必须异步执行否则使用 Tab 键产生的新节点无法获取焦点
            //setTimeout(() => {
                //refEl.current?.focus({preventScroll: true})
            //})

            refLastText.current = text
            refEl.current.textContent = text
            const range = document.createRange()
            range.selectNodeContents(refEl.current)
            const defaultText = map.getDefaultNodeText(node)

            if (defaultText !== text) {
                const rangeRect = range.getBoundingClientRect()
                const canvasRect = map.canvas.getBoundingClientRect()

                const {
                    width: scrollbarWidth,
                    height: scrollbarHeight,
                } = getScrollbarSize(map.canvas)

                const scrollOptions = {
                    left: Math.max(
                        0,
                        rangeRect.right - canvasRect.right + scrollbarWidth
                    ),

                    top: Math.max(
                        0,
                        rangeRect.bottom - canvasRect.bottom + scrollbarHeight
                    ),
                }

                map.canvas.scrollBy(scrollOptions)
                range.collapse(false)
            }

            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(range)
        },

        [isEditing]
    )

    const cancelEditText = () => {
        refEl.current.textContent = refLastText.current
        map.editNodeText(node, refLastText.current)
        map.finishEditNodeText(node)
    }

    const finishEditText = () => {
        refEl.current.textContent = text
        map.finishEditNodeText(node)
    }

    const onInput = e => {
        if (e.nativeEvent.isComposing) {
            return
        }

        map.editNodeText(node, e.target.textContent)
    }

    const stopPropagation = e => e.stopPropagation()

    const eventHandlers = isEditing ?
        {
            onBlur: finishEditText,
            onDoubleClick: stopPropagation,

            onKeyDown: e => {
                // 避免与其他快捷键冲突
                e.stopPropagation()

                if (e.nativeEvent.isComposing) {
                    return
                }

                // Enter/Esc 结束编辑
                if (! (e.ctrlKey || e.shiftKey)) {
                    if ('Enter' === e.key) {
                        e.preventDefault()
                        finishEditText()
                    }
                    else if ('Escape' === e.key) {
                        e.preventDefault()
                        cancelEditText()
                    }
                }
            },

            onKeyUp: stopPropagation,

            onPointerDown: e => {
                e.target.setPointerCapture(e.pointerId)
                e.stopPropagation()
            },

            onPointerMove: stopPropagation,
            onPointerUp: stopPropagation,
        }
        :
        {
            // 非编辑模式下输入非控制字符清空文本并进入编辑模式
            onKeyDown: e => {
                if (
                    'Process' === e.key ||

                    (
                        ! e.altKey &&
                        ! e.ctrlKey &&
                        ! e.metaKey &&
                        1 === e.key.length
                        ///^[\w `\-=[\]\\;',./~!@#$%^&*()_+{}|:"<>?]$/.test(e.key)
                    )
                ) {
                    map.startEditNodeText(node, '')
                }
            }
        }

    const style = isEditing ?
        {}
        :
        {
            position: 'absolute',
            zIndex: -1,
            opacity: 0,
            pointerEvents: 'none',
        }

    return (
        <div
            ref={refEl}
            style={style}
            contentEditable
            suppressContentEditableWarning
            onCompositionEnd={onInput}
            onInput={onInput}
            {...eventHandlers}
            {...props}
        >
        </div>
    )
}

const InputTextWrapper = ({node, ...props}) => {
    const map = useTreeDocContext()
    const isSelected = map.useIsNodeSelected(node)
    const canEditNodeText = map.canEditNodeText(node)

    if (
        ! isSelected ||
        ! canEditNodeText ||
        1 !== map.selectedNodes.size
    ) {
        return null
    }

    return (
        <InputText
            node={node}
            {...props}
        />
    )
}

const Text = ({isEditing, text, ...props}) => {
    if (isEditing) {
        return null
    }

    return <div {...props}>{text}</div>
}

const NodeText = ({node, ...props}) => {
    const map = useTreeDocContext()
    map.useNodeChange(node, ['data'])
    const isEditing = map.useIsEditingText(node)
    const style = map.useNodeTextStyle(node)
    const text = map.getNodeText(node)

    return (
        <div
            css={cssNodeText}
            style={style}
            {...props}
        >
            <InputTextWrapper
                isEditing={isEditing}
                node={node}
                text={text}
            />

            <Text
                isEditing={isEditing}
                text={text}
            />
        </div>
    )
}

const MemorizedNodeText = memo(NodeText, () => true)
MemorizedNodeText.displayName = 'NodeText'

export default MemorizedNodeText
